/* eslint-disable no-undef */
import React, { useMemo } from 'react';
import { checkMot } from '../../../../ressources/functionJS/checkMot';
import BibliothequePlotly from '../BibliothequePlotly/BibliothequePlotly';
import { convertiUtsToDate } from '../GrapheXT/GrapheXT';

const TableauTelt = ({
  configuration,
  donnees,
  viewobj_json_content,
  switchAfficherModifierIntervalle,
  dimensionWidth,
  dimensionHeight,
  donneesGraphe,
}) => {
  const tableData = useMemo(() => {
    // Vérification des données requises
    // eslint-disable-next-line no-undef
    if (!configuration || !donnees) {
      return { data: [], layout: {} };
    }

    // Configuration de la mise en page du tableau
    const layout = {
      font: { size: 14 },
      title: {
        text: configuration.title + '<br>' + configuration.subtitle,
        font: { color: '#3c3935' },
      },
      showlegend: true,
      legend: {
        orientation: 'h',
        x: 0,
        y: -0.0,
      },
      ...(viewobj_json_content?.layout || {}),
    };

    // Préparation des lignes du tableau
    let tableRows = [];

    // Parcourir toutes les courbes de données
    for (let curve of donnees) {
      // Trouver l'index de la valeur maximale
      const maxIndex = curve.y.indexOf(Math.max(...curve.y));
      const maxValue = curve.y[maxIndex];
      const maxDate = curve.x[maxIndex];

      // Récupérer la dernière valeur et sa date
      const latestValue = curve.y[curve.y.length - 1];
      const latestDate = curve.x[curve.x.length - 1];

      // Créer une ligne avec: nom, dernière valeur, date dernière valeur, valeur max, date max
      let newRow = [
        curve.name,
        latestValue,
        convertiUtsToDate([latestDate]),
        maxValue,
        convertiUtsToDate([maxDate]),
      ];

      tableRows.push(newRow);
    }

    // Déterminer l'unité d'ingénierie à partir de la première courbe
    const engineeringUnit =
      donnees.length > 0 ? donnees[0].engineering_unit : '';

    // Définir les en-têtes du tableau
    const tableHeaders = [
      checkMot('Capteur'),
      `Dernière valeur [${engineeringUnit}]`,
      'Date dernière valeur',
      `Valeur MAX [${engineeringUnit}]`,
      'Date Valeur MAX',
    ];

    // Transposer les lignes en colonnes pour Plotly
    // Plotly nécessite les données organisées par colonnes, pas par lignes
    const transposedData = tableHeaders.map((_, colIndex) =>
      tableRows.map((row) => row[colIndex])
    );

    // Configuration du tableau depuis le JSON ou valeurs par défaut
    const tableConfig = viewobj_json_content?.tableConfig || {};

    // Valeurs par défaut
    const defaultHeaderColor = '#EFB805';
    const defaultColumnWidth = [3, 3, 3, 3, 3];

    // Configuration finale du tableau Plotly
    const data = [
      {
        type: 'table',
        // Ordre et largeur des colonnes
        columnorder: tableConfig.columnorder,
        columnwidth: tableConfig.columnwidth || defaultColumnWidth,

        // Configuration de l'en-tête
        header: {
          values: tableHeaders,
          align: tableConfig.header?.align || ['left', 'center'],
          height: tableConfig.header?.height || 30,
          line: {
            width: tableConfig.header?.line?.width ?? 1,
            color: tableConfig.header?.line?.color || '#000000',
          },
          fill: {
            color: tableConfig.header?.fill?.color || defaultHeaderColor,
          },
          font: {
            family: tableConfig.header?.font?.family || 'Arial',
            size: tableConfig.header?.font?.size || 17,
            color: tableConfig.header?.font?.color || 'white',
          },
          // Format des valeurs (ex: ["", ".2f", "", ".2f", ""])
          format: tableConfig.header?.format,
          prefix: tableConfig.header?.prefix,
          suffix: tableConfig.header?.suffix,
        },

        // Configuration des cellules
        cells: {
          values: transposedData,
          align: tableConfig.cells?.align || ['left', 'center'],
          height: tableConfig.cells?.height || 25,
          line: {
            width: tableConfig.cells?.line?.width ?? 1,
            color: tableConfig.cells?.line?.color || '#000000',
          },
          fill: {
            // Peut être une couleur ou un tableau pour alterner les couleurs des lignes
            color: tableConfig.cells?.fill?.color || 'white',
          },
          font: {
            family: tableConfig.cells?.font?.family || 'Arial',
            size: tableConfig.cells?.font?.size || 17,
            color: tableConfig.cells?.font?.color || '#506784',
          },
          // Format des valeurs par colonne (ex: ["", ".2f", "", ".2f", ""])
          format: tableConfig.cells?.format,
          prefix: tableConfig.cells?.prefix,
          suffix: tableConfig.cells?.suffix,
        },
      },
    ];

    return { data, layout };
  }, [configuration, donnees, viewobj_json_content]);

  return (
    <BibliothequePlotly
      data={tableData.data}
      layout={{
        ...tableData.layout,
        responsive: true,
        ...viewobj_json_content,
      }}
      donneesGraphe={donneesGraphe}
      ouvrirPopupModifierIntervalle={switchAfficherModifierIntervalle}
      dimensionWidth={dimensionWidth}
      dimensionHeight={dimensionHeight}
      viewobj_json_content={viewobj_json_content}
    />
  );
};

export default TableauTelt;
